import { useMemo, useState } from "react"
import {
  Box,
  Flex,
  Grid,
  Text,
  VStack,
} from "@chakra-ui/react"
import { Graph, Figure } from "@hydrologiq/schemas/1.0.0/installationPage"
import { AssetConfig } from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"
import { FIXED_MONITORING_GRAPHS, type TimescaleGraphTemplate } from "@hydrologiq/shared/monitoring/graphTemplates"
import TimescaleTimeSeriesGraph from "./TimescaleTimeSeriesGraph"
import {
  buildLinearTimeAxisTicksMs,
  calculateDynamicAxisInterval,
} from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"
import { boundarySourceFromInstallation } from "@hydrologiq/shared/monitoring/builders/2.0.0/TimeRange"
import { useTimeRangeController } from "@hydrologiq/shared/monitoring/builders/2.0.0/useTimeRangeController"
import { useAutoRefresh } from "@hydrologiq/shared/monitoring/builders/2.0.0/useAutoRefresh"
import { TimeRangeControl } from "@hydrologiq/shared/monitoring/builders/2.0.0/TimeRangeControl"
import { mapRangeGranularity } from "@hydrologiq/shared/monitoring/builders/2.0.0/RangeGranularity"
import { Section, SectionRoot } from "@hydrologiq/shared/chakra/custom/Section"
import { useSystemMonitoringGraphs } from "../../hooks/useSystemMonitoringGraphs"
import type { GraphRequest } from "@hydrologiq/gql-experience/graphql"
import { batchPayloadHasNumericData, getTimescaleBatchGraphId, withoutEmptyTankGraphs } from "../../utils/timescaleBatchGraph"
import type { SystemTimescaleField } from "../../types/SystemView"
import InstallationHeadlineFigures from "@hydrologiq/shared/installation/InstallationHeadlineFigures"
import { toUserTZ } from "@hydrologiq/common/helpers/date"
import { useGetPref, useSetPref } from "@hydrologiq/shared/providers/helpers/usePref"

interface TimescaleMonitoringViewProps {
  systemId: string
  timescaleFields: SystemTimescaleField[]
  graphs?: Graph[]
  assetConfigs?: AssetConfig[]
  installationStartDate?: string
  installationEndDate?: string
  /** Limit Timescale rows to these connection ids; omit for core rows only (connection IS NULL) */
  timescaleConnections?: string[]
  /** Installation id for GetInstallationHeadlines (shared InstallationHeadlineFigures); omit to hide headline strip */
  installationId?: string
  figures?: Figure[]
  /** Available asset classes in the system; used to filter graphs by class */
  availableAssetClasses?: string[]
  /** Custom headline content to render instead of the default InstallationHeadlineFigures.
   *  When provided, replaces the built-in headline strip (installationId is still used to
   *  determine whether the strip section is visible). */
  headlineSlot?: React.ReactNode
}

export { FIXED_MONITORING_GRAPHS, type TimescaleGraphTemplate } from "@hydrologiq/shared/monitoring/graphTemplates"

const TimescaleMonitoringView = ({
  systemId,
  timescaleFields,
  graphs: customGraphs,
  assetConfigs,
  installationStartDate,
  installationEndDate,
  timescaleConnections,
  installationId,
  figures,
  availableAssetClasses,
  headlineSlot,
}: TimescaleMonitoringViewProps) => {
  const getPref = useGetPref()
  const setPref = useSetPref()

  const boundarySource = useMemo(
    () => boundarySourceFromInstallation(installationStartDate, installationEndDate),
    [installationStartDate, installationEndDate]
  )
  const browserTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, [])

  const { applied, apply, refresh, refreshKey } = useTimeRangeController({
    boundarySource,
    browserTimeZone,
    persistedPreset: getPref("timescalePeriod"),
    /** Persist the relative preset so it survives page navigation; Custom ranges are session-only. */
    onRelativePresetApplied: (preset) => setPref("timescalePeriod", preset),
  })
  const autoRefresh = useAutoRefresh({ apply, refresh, enabled: applied.mode === "relative" })
  const startDate = applied.startUtc
  const endDate = applied.endUtc

  const [graphOverlayState, setGraphOverlayState] = useState<Map<string, Set<string>>>(() => {
    const initialState = new Map<string, Set<string>>()
    FIXED_MONITORING_GRAPHS.forEach((template) => {
      const graphId = getTimescaleBatchGraphId(template as Graph)
      const defaultOverlays = template.availableOverlays
        ?.filter((o) => o.defaultEnabled)
        .map((o) => o.graphId)
      if (defaultOverlays?.length) {
        initialState.set(graphId, new Set(defaultOverlays))
      }
    })
    return initialState
  })

  const graphs = useMemo(() => {
    if (customGraphs && customGraphs.length > 0) {
      return customGraphs.map(
        (graph) =>
          ({
            ...graph,
            source: {
              ...graph.source,
              type: "TimescaleDB" as const,
            },
          }) as Graph
      )
    }

    let templates: TimescaleGraphTemplate[] = FIXED_MONITORING_GRAPHS

    if (availableAssetClasses) {
      const classSet = new Set(availableAssetClasses)
      templates = templates.filter((t) => {
        // If the graph filters by class, only include if that class is available
        if (t.filter?.class) {
          return classSet.has(t.filter.class)
        }
        // Role-based filters or no filter → always include
        return true
      })
    }

    // Filter by timescaleFields: only show graphs whose source.field is available
    if (timescaleFields && timescaleFields.length > 0) {
      const fieldSet = new Set(timescaleFields.map((f) => f.field))
      templates = templates.filter((t) => {
        const sourceField = t.source?.field
        if (!sourceField) return true
        return fieldSet.has(sourceField)
      })
    }

    return templates as Graph[]
  }, [customGraphs, availableAssetClasses, timescaleFields])

  const rangeTimescale = useMemo(() => mapRangeGranularity(startDate, endDate), [startDate, endDate])

  const graphsWithTimeseries = useMemo(() => {
    return graphs.map((graph) => ({
      graph,
      timeseries: rangeTimescale.timeseries,
    }))
  }, [graphs, rangeTimescale.timeseries])

  const batchGranularity = rangeTimescale.granularity

  const graphRequests: GraphRequest[] = useMemo(() => {
    const requests: GraphRequest[] = []

    graphs.forEach((graph) => {
      const graphId = getTimescaleBatchGraphId(graph)
      const field = graph.source.field
      const ext = graph as Graph & {
        aggregation?: string
        aggregationRole?: string
        dualMetric?: boolean
        filter?: { role?: string; class?: string }
        batteryView?: boolean
        includeConnections?: boolean
        connectionsOnly?: boolean
      }
      const filter = ext.filter
      const hasAgg = ext.aggregation === "system"
      const dual = ext.dualMetric === true
      const useFiltered = Boolean(filter?.role || filter?.class || hasAgg)
      const isBatteryView = ext.batteryView === true
      /** Backend splits breakdown by connection only when this (or connections / connectionsOnly) is set. */
      const includeConnections =
        ext.includeConnections === true ||
        ext.connectionsOnly === true ||
        ext.aggregationRole === "load" ||
        (filter?.role === "consumer" && field === "power")
      const connectionsOnly = ext.connectionsOnly === true

      const template = graph as Graph & { availableOverlays?: Array<{ graphId: string }> }
      const overlayGraphIds = template.availableOverlays?.length
        ? template.availableOverlays.map((o) => o.graphId)
        : null

      if (isBatteryView) {
        requests.push({
          graphId: `${graphId}_discharge`,
          field: "power",
          role: null,
          assetClass: filter?.class ?? null,
          includeAggregated: false,
          includeBreakdown: true,
          secondaryField: null,
          connectionsOnly: true,
        })
        requests.push({
          graphId: `${graphId}_charge`,
          field: "upstream_power",
          role: null,
          assetClass: filter?.class ?? null,
          includeAggregated: false,
          includeBreakdown: true,
          secondaryField: null,
          connectionsOnly: true,
        })
        requests.push({
          graphId: `${graphId}_soc`,
          field: "soc",
          role: null,
          assetClass: filter?.class ?? null,
          includeAggregated: false,
          includeBreakdown: true,
          secondaryField: null,
        })
      } else {
        const isLoadGraph = filter?.role === "consumer" && ext.aggregationRole === "load"
        const req: GraphRequest = {
          graphId,
          field,
          role: filter?.role ?? null,
          assetClass: filter?.class ?? null,
          includeAggregated: hasAgg,
          includeBreakdown: true,
          // The fuel series is signed in the database: a bucket holds the fall
          // in tank level since the previous one, so movement within the
          // sender's tolerance leaves small negatives. `fuel_consumed_displayed`
          // is the same series with those settled into their neighbours, which
          // is what a chart can draw. The signed measurement is still exported
          // in the CSV for analysis.
          secondaryField: dual ? "fuel_consumed_displayed" : null,
          includeConnections,
          connectionsOnly,
          ...(ext.aggregationRole ? { aggregationRole: ext.aggregationRole } : {}),
          ...(overlayGraphIds ? { overlayGraphIds } : {}),
          ...(isLoadGraph ? { includePhaseBalance: true } : {}),
        }
        if (!useFiltered && timescaleConnections?.length) {
          req.connections = timescaleConnections
        }
        requests.push(req)
      }
    })

    return requests
  }, [graphs, timescaleConnections])

  const { graphsData, loading: batchLoading, error: batchError } =
    useSystemMonitoringGraphs(
      {
        systemId,
        start: startDate.toIsoString(),
        stop: endDate.toIsoString(),
        granularity: batchGranularity,
        refreshKey,
        graphs: graphRequests,
      },
      assetConfigs
    )

  /** Compute unified timestamp set across all graphs for consistent x-axis alignment */
  const sharedTimestamps = useMemo(() => {
    if (batchLoading || graphsData.size === 0) return undefined

    const timestampSet = new Set<string>()

    for (const payload of graphsData.values()) {
      // Collect timestamps from breakdown data
      if (payload.breakdown?.data) {
        payload.breakdown.data.forEach((point) => {
          if (point.timestamp) {
            timestampSet.add(point.timestamp as string)
          }
        })
      }

      // Collect timestamps from aggregated data
      if (payload.aggregated?.data) {
        payload.aggregated.data.forEach((point) => {
          if (point.timestamp) {
            timestampSet.add(point.timestamp as string)
          }
        })
      }
    }

    return Array.from(timestampSet).sort()
  }, [graphsData, batchLoading])



  const sharedXAxisInterval = useMemo(() => {
    if (!sharedTimestamps?.length) return undefined
    return calculateDynamicAxisInterval(startDate, endDate, sharedTimestamps.length)
  }, [startDate, endDate, sharedTimestamps])

  /** Same tick times (UTC ms) on every graph including battery — avoids per-chart minTickGap drift */
  const sharedXAxisTicksMs = useMemo(
    () => buildLinearTimeAxisTicksMs(startDate, endDate, 8),
    [startDate, endDate]
  )

  /** Fuel and storage graphs with no data are hidden, each on its own. */
  const graphsWithTimeseriesToRender = useMemo(() => {
    if (batchLoading) return graphsWithTimeseries
    return withoutEmptyTankGraphs(graphsWithTimeseries, (graph) => graphsData.get(getTimescaleBatchGraphId(graph)))
  }, [graphsWithTimeseries, batchLoading, graphsData])
  const headlineStop = useMemo(
    () => (installationEndDate ? toUserTZ(installationEndDate) : undefined),
    [installationEndDate]
  )

  const showHeadlineStrip = Boolean(installationId)

  return (
    <SectionRoot>
      <Section separator={false}>
        <Flex direction="column" gap={0} p={0}>
          {showHeadlineStrip ? (
            <Box width="100%">
              {headlineSlot ?? (
                <InstallationHeadlineFigures
                  installationId={installationId}
                  stop={headlineStop}
                  refreshVersion={refreshKey}
                  figures={figures}
                />
              )}
            </Box>
          ) : null}

          <Flex
            width="100%"
            mt={showHeadlineStrip ? { base: 3, md: 5 } : { base: 2, md: 4 }}
          >
            <TimeRangeControl
              applied={applied}
              boundarySource={boundarySource}
              browserTimeZone={browserTimeZone}
              onApply={autoRefresh.apply}
              onOpenChange={autoRefresh.onOpenChange}
            />
          </Flex>

          <Grid
            templateColumns={{ base: "1fr", lg: "1fr" }}
            gap={{ base: 6, md: 8 }}
            mt={{ base: 6, md: 8 }}
            width="100%"
            minW={0}
          >
            {!batchLoading && graphsData.size > 0 && !Array.from(graphsData.values()).some(batchPayloadHasNumericData) ? (
              <Box
                gridColumn="1 / -1"
                bg="card"
                borderRadius="5px"
                boxShadow="card"
                p={{ base: 8, md: 12 }}
                textAlign="center"
              >
                <VStack gap={3}>
                  <Text fontSize="lg" fontWeight="medium" color="fg.muted">
                    Waiting for data
                  </Text>
                  <Text fontSize="sm" color="fg.subtle" maxW="420px">
                    If you have recently connected a data source, data should be available within 10 minutes. Relative time ranges update automatically every five minutes.
                  </Text>
                </VStack>
              </Box>
            ) : (
              graphsWithTimeseriesToRender.map(({ graph, timeseries }) => {
              const graphId = getTimescaleBatchGraphId(graph)
              const template = graph as Graph & {
                availableOverlays?: TimescaleGraphTemplate["availableOverlays"]
                batteryView?: boolean
              }
              const batchData = graphsData.get(graphId)

              return (
                <Box
                  key={graphId}
                  gridColumn={graph.size === "full" ? "1 / -1" : undefined}
                  minW={0}
                  bg="card"
                  borderRadius="5px"
                  boxShadow="card"
                  p={4}
                  minHeight="100px"
                >
                  <TimescaleTimeSeriesGraph
                    graph={graph}
                    timeseries={timeseries}
                    startDate={startDate}
                    endDate={endDate}
                    chartAxisStartDate={startDate}
                    chartAxisEndDate={endDate}
                    assetConfigs={assetConfigs}
                    graphBatchData={batchData}
                    batchLoading={batchLoading}
                    batchError={batchError}
                    availableOverlays={template.availableOverlays}
                    activeOverlays={graphOverlayState.get(graphId) || new Set()}
                    onOverlayToggle={(overlayId, enabled) => {
                      setGraphOverlayState((prev) => {
                        const next = new Map(prev)
                        const current = next.get(graphId) || new Set()
                        if (enabled) {
                          current.add(overlayId)
                        } else {
                          current.delete(overlayId)
                        }
                        next.set(graphId, new Set(current))
                        return next
                      })
                    }}
                    batteryView={template.batteryView}
                    graphsData={graphsData}
                    sharedTimestamps={sharedTimestamps}
                    xAxisTickInterval={sharedXAxisInterval}
                    xAxisTicksMs={sharedXAxisTicksMs}
                    groupedAssetMenu
                    selectOneAsset
                    perAssetView
                  />
                </Box>
              )
            }))
            }
          </Grid>
        </Flex>
      </Section>
    </SectionRoot>
  )
}

export default TimescaleMonitoringView
